import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Tooltip } from "@mui/material";
import { EditLinkButton } from "Components/Shared/Buttons/EditLinkButton";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { PhoneVerificationDialog } from "Components/Shared/PhoneVerification/PhoneVerificationDialog";
import { PublicInfoCard } from "Components/Social/PublicInfoCard";
import { PhoneForm } from "Components/UserAccount/Shared/PublicInformation/PhoneForm";
import { usePhoneVerificationStatus } from "Hooks/usePhoneVerificationStatus";
import { type PartyDto, PartyType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatPhone } from "Utils/PhoneUtils";
import { useState } from "react";

const RESOURCES = Resources.Social.PublicPermissions;

type Props = {
  party: PartyDto;
};

/**
 * Phone card of the public-information tab. Person parties (NaturalPerson/SelfEmployed — the
 * same set the backend verifies against) get a single change-and-verify flow: the one action
 * button opens the shared OTP dialog, which saves the typed number as both Phone and
 * VerifiedPhone on success — there is no separate unverified save (and thus no way to clear
 * the number here, by design). The badge comes from the server-computed status (current person
 * phone == OTP-verified one — never party.verifiedPhone, which is display-only).
 *
 * LegalEntity parties (merchant company) keep the plain edit dialog and carry no verification
 * affordances — verification is person-level and a person badge next to a company phone would
 * mislead.
 */
export const PhoneCard = ({ party }: Props) => {
  const { t } = useResource();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isPersonParty = party.type !== PartyType.LegalEntity;

  const {
    isLoading: isVerificationLoading,
    isVerified,
    phone: verificationPhone,
    hasPhone: hasVerificationPhone,
  } = usePhoneVerificationStatus();

  const isVerificationReady = isPersonParty && !isVerificationLoading;

  return (
    <>
      <PublicInfoCard
        label={t(Resources.Common.Phone)}
        value={formatPhone(party.phone) || null}
        valueAdornment={
          isVerificationReady &&
          (isVerified ? (
            <Tooltip title={t(RESOURCES.PhoneVerifiedTooltip)}>
              <VerifiedIcon color="success" fontSize="small" />
            </Tooltip>
          ) : (
            <Tooltip title={t(RESOURCES.PhoneUnverifiedTooltip)}>
              <WarningAmberIcon color="warning" fontSize="small" />
            </Tooltip>
          ))
        }
        action={
          isPersonParty ? (
            isVerificationReady && (
              <EditLinkButton onClick={() => setIsDialogOpen(true)}>
                {isVerified ? undefined : t(RESOURCES.VerifyAction)}
              </EditLinkButton>
            )
          ) : (
            <EditLinkButton
              onClick={() => setIsDialogOpen(true)}
              isDisabled={!party.publicID}
            />
          )
        }
      />
      {isPersonParty ? (
        // Mounted only while open: the dialog's form captures defaultPhone on mount (the status
        // query may resolve later), and an abandoned session (cancel at the code step) must not
        // resurface mid-flow on reopen.
        isDialogOpen && (
          <PhoneVerificationDialog
            isOpen
            defaultPhone={verificationPhone ?? undefined}
            hasPhone={hasVerificationPhone}
            onClose={() => setIsDialogOpen(false)}
            description={t(Resources.Common.PhoneVerificationDescription)}
          />
        )
      ) : (
        <ResponsiveDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          title={t(Resources.Common.Phone)}
        >
          <PhoneForm party={party} onSuccess={() => setIsDialogOpen(false)} />
        </ResponsiveDialog>
      )}
    </>
  );
};
